import React, { useEffect, useState } from "react";
import { Button } from "@heathmont/moon-core-tw";
import { ControlsPlus } from "@heathmont/moon-icons-tw";
import PageTitle from "../../Components/layouts/header/page-title";
import LeaveDrawer from "./widgets/LeaveDrawer";
import { useSelector } from "react-redux";
import { RootState, store } from "../../store/store";
import { getMyLeaves, createLeaveRequest, cancelLeave } from "../../services/employee.service";
import {
  setSnackBar,
  setToastMessage,
} from "../../features/ToastSlice/toastSlice";
import moment from "moment";
import { notificationSocket } from "../../utils/notification.socket.service";
import { apiBaseUrl } from "../../constants/environmentConstants/environmentConstants";

const LEAVE_TYPE_LABELS: Record<string, string> = {
  AL: "Annual Leave",
  SL: "Sick Leave",
  CO: "Compensatory Off",
  ML: "Maternity Off",
  LL: "Learning Leave",
  CL: "Condolence Leave",
  PL: "Paternity Leave",
  UL: "Unpaid Leave",
};

const STATUS_CLASSES: Record<string, string> = {
  Pending:   "bg-yellow-100 text-yellow-700",
  Accepted:  "bg-green-100 text-green-700",
  Rejected:  "bg-red-100 text-red-700",
  Cancelled: "bg-gray-100 text-gray-500",
};

const Leaves = () => {
  const { User } = useSelector((state: RootState) => state.auth);
  const currentYear = Number(moment().format("YYYY"));

  const [leaves, setLeaves] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [cancelTarget, setCancelTarget] = useState<any>(null); // leave being confirmed for cancel
  const [isCancelling, setIsCancelling] = useState<boolean>(false);

  const fetchLeaves = async () => {
    setIsLoading(true);
    // Fetch previous, current, and next year so all leaves appear regardless of when applied
    const years = [currentYear - 1, currentYear, currentYear + 1];
    const results = await Promise.all(years.map((y) => getMyLeaves(y)));
    const combined: any[] = results.flatMap((res: any) => res?.res ?? []);
    const sorted = combined.sort(
      (a: any, b: any) =>
        moment(b.from?.date, "MM/DD/YYYY").valueOf() -
        moment(a.from?.date, "MM/DD/YYYY").valueOf()
    );
    setLeaves(sorted);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  // Re-fetch when manager accepts or declines this nurse's leave request
  useEffect(() => {
    if (!User?.id || !apiBaseUrl) return;

    // Ensure socket is initialized
    if (!notificationSocket.socket?.connected) {
      notificationSocket.initializeConnection(apiBaseUrl, User.id, User.role);
    }

    const handler = () => {
      console.log("[Leaves] leaveResponse received — refreshing");
      fetchLeaves();
    };

    // Retry attaching the listener until socket is ready
    let retries = 0;
    const attach = () => {
      if (notificationSocket.socket) {
        notificationSocket.socket.off("leaveResponse", handler);
        notificationSocket.socket.on("leaveResponse", handler);
        console.log("[Leaves] leaveResponse listener attached");
      } else if (retries < 10) {
        retries++;
        setTimeout(attach, 300);
      }
    };
    attach();

    return () => {
      notificationSocket?.socket?.off("leaveResponse", handler);
    };
  }, [User?.id]);

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setIsCancelling(true);
    const res: any = await cancelLeave(cancelTarget._id);
    setIsCancelling(false);
    setCancelTarget(null);
    if (res?.res) {
      store.dispatch(setSnackBar("success"));
      store.dispatch(setToastMessage("Leave cancelled successfully"));
      fetchLeaves();
    } else {
      store.dispatch(setSnackBar("error"));
      store.dispatch(setToastMessage(res?.err?.message || "Failed to cancel leave"));
    }
  };

  const handleSubmit = async (
    leaveType: string,
    fromDate: string,
    toDate: string,
    reason: string
  ) => {
    const from = {
      date: moment(fromDate).format("MM/DD/YYYY"),
      dayNumber: moment(fromDate).date(),
      month: moment(fromDate).month() + 1,
      year: moment(fromDate).year(),
    };
    const to = {
      date: moment(toDate).format("MM/DD/YYYY"),
      dayNumber: moment(toDate).date(),
      month: moment(toDate).month() + 1,
      year: moment(toDate).year(),
    };

    const res: any = await createLeaveRequest({
      employeeId: User?.employeeId,
      employeeName: User?.employeeName,
      leaveType,
      from,
      to,
    });

    if (res?.res) {
      store.dispatch(setSnackBar("success"));
      store.dispatch(setToastMessage("Leave request submitted successfully"));
      setIsDrawerOpen(false);
      fetchLeaves();
    } else {
      store.dispatch(setSnackBar("error"));
      store.dispatch(
        setToastMessage(res?.err?.message || "Failed to submit leave request")
      );
    }
  };

  return (
    <>
      <div className="bg-[#f8f8f8] min-h-screen">
        <PageTitle
          title="Leaves"
          subtitle="Apply and view your leave requests"
        />

        <div className="p-[15px] md:p-[20px_30px] pb-20 md:pb-6">
          {/* Top bar */}
          <div className="flex flex-wrap items-center justify-end gap-3 mb-6">
            <Button
              variant="tertiary"
              onClick={() => setIsDrawerOpen(true)}
            >
              <ControlsPlus className="text-moon-24" />
              Apply Leave
            </Button>
          </div>

          {/* Leave list */}
          {isLoading ? (
            <div className="flex justify-center items-center h-48 text-gray-400 text-moon-14">
              Loading...
            </div>
          ) : leaves.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <img
                src={"images/no-schedule-to-approve.svg"}
                alt="No leaves"
                className="mb-4"
              />
              <p className="text-gray-400 text-moon-14">No leave requests found</p>
            </div>
          ) : (
            <div className="bg-white rounded-moon-s-lg shadow-sm overflow-x-auto">
              {/* Table header */}
              <div className="grid grid-cols-6 gap-4 px-6 py-3 bg-gray-50 border-b border-beerus text-moon-12 font-semibold text-gray-500 uppercase tracking-wide min-w-[560px]">
                <div>Leave Type</div>
                <div>From</div>
                <div>To</div>
                <div>Duration</div>
                <div>Status</div>
                <div></div>
              </div>

              {/* Rows */}
              {leaves.map((leave: any) => {
                const fromMoment = moment(leave.from?.date, "MM/DD/YYYY");
                const toMoment = moment(leave.to?.date, "MM/DD/YYYY");
                const duration = toMoment.diff(fromMoment, "days") + 1;
                const canCancel = leave.status === "Pending" || leave.status === "Accepted";

                return (
                  <div
                    key={leave._id}
                    className="grid grid-cols-6 gap-4 px-6 py-4 border-b border-beerus hover:bg-gray-50 transition-colors items-center min-w-[560px]"
                  >
                    <div className="text-moon-14 font-medium text-blackcst">
                      {LEAVE_TYPE_LABELS[leave.leaveType] || leave.leaveType}
                    </div>
                    <div className="text-moon-14 text-gray-600">
                      {fromMoment.format("DD MMM YYYY")}
                    </div>
                    <div className="text-moon-14 text-gray-600">
                      {toMoment.format("DD MMM YYYY")}
                    </div>
                    <div className="text-moon-14 text-gray-600">
                      {duration} day{duration !== 1 ? "s" : ""}
                    </div>
                    <div>
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-moon-12 font-medium ${
                          STATUS_CLASSES[leave.status] || "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {leave.status}
                      </span>
                    </div>
                    <div>
                      {canCancel && (
                        <button
                          onClick={() => setCancelTarget(leave)}
                          className="text-moon-12 text-red-500 hover:text-red-700 hover:underline font-medium transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Drawer */}
      <LeaveDrawer
        isOpen={isDrawerOpen}
        setIsOpen={setIsDrawerOpen}
        handleClose={() => setIsDrawerOpen(false)}
        onSubmit={handleSubmit}
      />

      {/* Cancel confirmation modal */}
      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-moon-s-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-moon-18 font-semibold text-gray-900 mb-2">
              Cancel Leave Request
            </h3>

            {cancelTarget.status === "Accepted" && (
              <div className="mb-4 p-3 rounded-md bg-yellow-50 border border-yellow-200 text-moon-14 text-yellow-800">
                ⚠️ This leave has already been <strong>approved</strong>. Cancelling it will notify your nurse manager and may require a schedule update.
              </div>
            )}

            <p className="text-moon-14 text-gray-600 mb-6">
              Are you sure you want to cancel your{" "}
              <strong>{LEAVE_TYPE_LABELS[cancelTarget.leaveType] || cancelTarget.leaveType}</strong>{" "}
              from{" "}
              <strong>{moment(cancelTarget.from?.date, "MM/DD/YYYY").format("DD MMM YYYY")}</strong>{" "}
              to{" "}
              <strong>{moment(cancelTarget.to?.date, "MM/DD/YYYY").format("DD MMM YYYY")}</strong>?
            </p>

            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setCancelTarget(null)}
                disabled={isCancelling}
              >
                Keep Leave
              </Button>
              <button
                onClick={handleCancelConfirm}
                disabled={isCancelling}
                className="px-4 py-2 rounded-moon-s-sm bg-red-500 hover:bg-red-600 text-white text-moon-14 font-medium transition-colors disabled:opacity-50"
              >
                {isCancelling ? "Cancelling..." : "Yes, Cancel Leave"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Leaves;
